/**
 * `GET /api/dashboard-data` → the props the `Dashboard` page can state truthfully.
 *
 * **The server's whole answer** (`dashboard.service.js:176-183`):
 *
 *     { users: [{ id, username, firstName, avatarUrl, weeklyReward, chores: [...] }],
 *       unassignedChores: [...],
 *       meta: { timestamp, timezone, isAdmin } }
 *
 * **The trap that shapes this module.** `dashboard.service.js:109` selects completions with
 * `tracked_time >= NOW() - INTERVAL '90 seconds'`, into a variable named `allCompletedToday`.
 * The name says today; the query says ninety seconds. So `chores[].completed === true` means
 * "finished in the last minute or two", NOT "finished today".
 *
 * Two consequences, and both are the reason this module exists rather than four lines in the
 * container:
 *
 *  1. **No "today" count is derivable**, and it would surface in TWO places with two different
 *     strings — a distinction the first version of this docblock got wrong, which then produced
 *     a guard aimed at only one of them:
 *       - `QuestCard.jsx:857-863` renders `` `${lit} of ${total} lit` `` → **"3 of 8 lit"**
 *       - `Dashboard.jsx:263` (`composeBandSubtitle`) renders **"6 of 8 quests lit today"**
 *     Neither number can be honestly filled from a ninety-second window, so `litCount` and
 *     `totalCount` are left `undefined`, which the card documents as "hide this".
 *  2. **The activity feed is a "just now" feed**, and it has to say so, because its own default
 *     empty label is "No activity yet — completed quests will appear here." A family that
 *     finished twelve quests this morning would read that sentence.
 *
 * `undefined` is never used to mean "hide" where a component defaults the value — `undefined`
 * re-selects the default. `null` is used where the page reads it as "not wired".
 */

/** A number the server actually sent, or `null`. Never a zero invented for an absent field. */
function realNumber(value) {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function realText(value) {
  return typeof value === 'string' && value.trim() !== '' ? value.trim() : null;
}

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

/**
 * The name a member is shown by on this board (#583 D-B review H1). The server sends the one rule —
 * first name, else display name, never the username — in `firstName`, `null` when the member has
 * neither. The username is NOT a fallback here either: a grown-up's can be an email address. A
 * nameless member keeps a tile, named the way the Trophies picker names them.
 */
function memberName(user) {
  return realText(user && user.firstName) || (user && user.id != null ? `Member ${user.id}` : null);
}

/**
 * One family member's card.
 *
 * `recap` is the only free-text line the dashboard `QuestCard` renders.
 *
 * **It does NOT say "Next up".** `dashboard.service.js:74-85` selects only chores that are
 * ALREADY DUE — hourly with `next_estimated_execution_time <= NOW()`, or non-hourly due today or
 * earlier — and orders them `display_order ASC, chore_id ASC`. There is no future schedule and no
 * time ordering anywhere in the payload, so "next" would assert an order the server never
 * computed: a family with an overdue "Dishes" at `display_order` 5 and a due-today "Homework" at
 * `display_order` 1 would be told "Next up: Homework".
 *
 * So the line says **due**, which is what the query means, and it picks the OLDEST by
 * `nextExecutionTime` — the one that has waited longest — rather than whichever happened to sort
 * first. When more than one is due it says so, because naming one and hiding four is its own
 * quiet false impression.
 */
export function toMemberCard(user) {
  if (!user || typeof user !== 'object') return null;
  const chores = asArray(user.chores);
  const due = chores.filter((c) => c && c.completed !== true);
  // Oldest first. A chore with no parsable time sorts last rather than winning by accident.
  const ordered = [...due].sort((a, b) => {
    const ta = Date.parse(a && a.nextExecutionTime);
    const tb = Date.parse(b && b.nextExecutionTime);
    if (Number.isNaN(ta) && Number.isNaN(tb)) return 0;
    if (Number.isNaN(ta)) return 1;
    if (Number.isNaN(tb)) return -1;
    return ta - tb;
  });
  const first = ordered[0] || null;
  const firstName = first ? realText(first.name) : null;
  const more = ordered.length - 1;

  return {
    id: user.id,
    name: memberName(user),
    avatar: realText(user.avatarUrl),
    // NOT litCount/totalCount. A ninety-second window cannot answer "how many today", and the
    // card would render them as "N of M lit".
    recap: firstName
      ? (more > 0 ? `Due now: ${firstName}, and ${more} more` : `Due now: ${firstName}`)
      : 'No quest due right now.',
    // `weeklyReward` is carried but DELIBERATELY NOT RENDERED, and that is the point: this
    // endpoint sends no currency, so any money on this screen would pick one. `Dashboard.jsx`
    // defaults `currency` to 'USD'. Keep it unrendered, or supply a real currency first.
    weeklyReward: realNumber(user.weeklyReward),
  };
}

/**
 * The "just now" feed rows, drawn from every member's completions.
 *
 * The server already bounds these to ninety seconds, so this does no time filtering of its own
 * — filtering here would silently narrow a window the caller cannot see.
 */
export function toJustNowFeed(payload) {
  const users = asArray(payload && payload.users);
  const rows = [];
  for (const user of users) {
    const who = memberName(user);
    for (const chore of asArray(user && user.chores)) {
      if (!chore || chore.completed !== true) continue;
      const what = realText(chore.name);
      if (!what || !who) continue;
      rows.push({
        id: chore.completionId != null ? `c${chore.completionId}` : `${user.id}-${chore.id}`,
        // `ActivityFeedTile.jsx:278` reads `actor` / `action` / `highlight`. It also reads
        // `amount`, which is NOT passed: `chore.reward` is a bare number and this endpoint
        // sends no currency, so rendering it would pick one.
        actor: who,
        action: 'finished',
        highlight: what,
        timestamp: realText(chore.trackedTime),
      });
    }
  }
  return rows;
}

/**
 * The whole mapping.
 *
 * Returns `null` for a payload that is not the documented shape, so the container can say the
 * read failed rather than render an empty family that was never read.
 */
export function toDashboard(payload) {
  if (!payload || typeof payload !== 'object') return null;
  if (!Array.isArray(payload.users)) return null;

  const meta = payload.meta && typeof payload.meta === 'object' ? payload.meta : {};
  const members = payload.users.map(toMemberCard).filter((m) => m && m.name);

  // `meta.isAdmin` is deliberately NOT returned. Nothing on this screen branches on it, and the
  // server already withholds other members' rows from a non-admin, so carrying it would invite a
  // client-side gate over a decision the server has already made.
  return {
    members,
    justNow: toJustNowFeed(payload),
    unassignedCount: asArray(payload.unassignedChores).length,
    // Consumed: the "Just now" rows are stamped in the family's zone, not the reader's.
    timezone: realText(meta.timezone),
  };
}

/**
 * The SAME endpoint, read for one member: `/today` (#30 / R7.2).
 *
 * **Why a caller id is required rather than optional.** `getDashboardData` returns every active
 * member for an admin and only the caller's row for a non-admin, and the response never says
 * which row is the caller — there is no `currentUserId` in it. Guessing "the only row" is right
 * for a child and wrong for a parent, who would get somebody else's quests. So the caller's id
 * comes from `GET /api/auth/verify`, and a payload with no matching row is a FAILED READ rather
 * than an empty evening.
 *
 * **There is no "tomorrow".** `dashboard.service.js:74-85` selects only chores already due, so
 * every quest here is due now. The upcoming section is left empty on purpose, and the page
 * already renders nothing for an empty one.
 *
 * @returns {null|{name, quests, timezone}} `null` means the payload could not be read for this
 *   member, which the container must treat as a failed read.
 */
export function toTodayForMember(payload, memberId) {
  if (!payload || typeof payload !== 'object') return null;
  if (!Array.isArray(payload.users)) return null;
  if (memberId == null) return null;

  const mine = payload.users.find((u) => u && u.id === memberId);
  if (!mine) return null;

  // An ABSENT or unreadable quest list is not an empty one. `asArray` turns `undefined`, `null`
  // and `{}` all into `[]`, and the first version of this mapper let every one of them mean
  // "nothing is due" — a statement about this child's evening from a field that was never read.
  // Found by the gpt-6-astra review, which drove all three shapes and watched the screen say
  // "Nothing is due right now. Live family data." with no retry offered.
  if (!Array.isArray(mine.chores)) return null;

  const meta = payload.meta && typeof payload.meta === 'object' ? payload.meta : {};
  // Oldest first, so the quest that has waited longest is at the top. Same ordering rule as the
  // dashboard card, and for the same reason: `display_order` is not a time.
  const due = asArray(mine.chores)
    .filter((c) => c && c.completed !== true)
    .sort((a, b) => {
      const ta = Date.parse(a && a.nextExecutionTime);
      const tb = Date.parse(b && b.nextExecutionTime);
      if (Number.isNaN(ta) && Number.isNaN(tb)) return 0;
      if (Number.isNaN(ta)) return 1;
      if (Number.isNaN(tb)) return -1;
      return ta - tb;
    });

  return {
    // No placeholder in a greeting: "Good evening ✦" rather than "Good evening, Member 7 ✦".
    name: realText(mine.firstName),
    quests: due.map((c) => ({
      id: c.id,
      title: realText(c.name) || 'Quest',
      // NOT `reward`. The card renders it as money and this endpoint sends no currency.
      // NOT `xp` or `streak` either — nothing here counts them.
      status: 'unlit',
    })),
    timezone: realText(meta.timezone),
  };
}

/**
 * The greeting word, from the FAMILY's clock rather than the reader's.
 *
 * `Today.jsx`'s `greetingWord` default is "Good evening", which is simply false at breakfast. The time of day
 * IS knowable — `meta.timezone` comes from the server and the current instant is real — so this
 * derives it rather than dropping the product's voice. With no zone it returns `null` and the
 * container greets without a time word at all, because an unknown zone is not an excuse to guess.
 */
export function greetingWordFor(timeZone, now = new Date()) {
  if (!timeZone) return null;
  let hour;
  try {
    hour = Number(new Intl.DateTimeFormat('en-US', {
      hour: 'numeric', hour12: false, timeZone,
    }).format(now));
  } catch {
    return null;
  }
  if (!Number.isFinite(hour)) return null;
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
}

export default toDashboard;
